import startCase from 'lodash/startCase';
import { Appearance } from 'design';
import {
  ApiQueryOperator,
  EmployeeActionRequiredType,
  EmployeeRole,
  EmployeeStatus,
  EmployeeType,
  IApiQueryParam,
  IEmployee,
  IInviteEmployee,
  IProcessedOnboardingEmployee,
  IProcessOnboardingEmployeesResponse,
  OnboardingEmployeeStatus,
  SiteRole,
} from 'types';

/**
 * Returns a string to display on the UI based on the employee type
 *
 * @param {EmployeeType} type - The type of the employee.
 * @returns {string} - Returns the string to display corresponding to the EmployeeType.
 *                     'User' for EmployeeType.USER and 'Org Admin' for EmployeeType.ADMIN.
 */
export const employeeTypeDisplay = (type: EmployeeType): string => {
  const userType: Record<EmployeeType, string> = {
    [EmployeeType.USER]: 'User',
    [EmployeeType.ADMIN]: 'Org Admin',
  };
  return userType[type];
};

/**
 * Returns a string to display on the UI for a given site role.
 *
 * @param {SiteRole} role - The role at a given site.
 *
 * @returns {string} - Returns the string to display corresponding to the site role.
 *                     'Employee' for SiteRole.EMPLOYEE, 'Manager' for SiteRole.MANAGER, and 'Site Admin' for SiteRole.ADMIN.
 */
export const roleSiteDisplay = (role: SiteRole): string => {
  const userType: Record<SiteRole, string> = {
    [SiteRole.EMPLOYEE]: 'Employee',
    [SiteRole.MANAGER]: 'Manager',
    [SiteRole.ADMIN]: 'Site Admin',
  };
  return userType[role];
};

/**
 * Returns a string to display on the UI based on an employee's type or role at a given site.
 *
 * @param {EmployeeType} type - The type of the employee.
 * @param {SiteRole} [role] - The role at a given site. Optional.
 *
 * @returns {string} - Returns the string to display according to the employee's type or role.
 *                     If the employee type is USER and a site role is provided, it returns the display string based on the role at a given site.
 *                     Otherwise, it returns the display string for the employee type.
 */
export const orgRoleDisplay = (type: EmployeeType, role?: SiteRole): string => (type === EmployeeType.USER && role ? roleSiteDisplay(role) : employeeTypeDisplay(type));

/**
 * Checks if an employee is an Organization Admin.
 *
 * @param {IEmployee | IInviteEmployee} employee - The employee object containing the employee type.
 * @returns {boolean} - Returns `true` if the employee's type is `EmployeeType.ADMIN`, otherwise `false`.
 */
export const isOrgAdmin = (employee: IEmployee | IInviteEmployee): boolean => employee.type === EmployeeType.ADMIN;

/**
 * A mapping of site roles to employee roles.
 *
 * @type {Record<SiteRole, EmployeeRole>}
 *
 * Maps each `SiteRole` to its corresponding `EmployeeRole`.
 * - `SiteRole.ADMIN` maps to `EmployeeRole.SITE_ADMIN`
 * - `SiteRole.MANAGER` maps to `EmployeeRole.MANAGER`
 * - `SiteRole.EMPLOYEE` maps to `EmployeeRole.EMPLOYEE`
 */
export const siteRoleToEmployeeRole: Record<SiteRole, EmployeeRole> = {
  [SiteRole.ADMIN]: EmployeeRole.SITE_ADMIN,
  [SiteRole.MANAGER]: EmployeeRole.MANAGER,
  [SiteRole.EMPLOYEE]: EmployeeRole.EMPLOYEE,
};

/**
 * Generates display properties for an employee's status.
 *
 * @param {EmployeeStatus} [status] - The status of the employee.
 * @param {EmployeeActionRequiredType} [actionRequired] - The specific action required, used when the status is `ACTION_REQUIRED`.
 * @returns {{ text: string; appearance: Appearance } | null} An object with `text` and `appearance` for the UI, or `null` if the status is not provided.
 */
export const getEmployeeStatusDisplayProps = (status?: EmployeeStatus, actionRequired?: EmployeeActionRequiredType): { text: string; appearance: Appearance } | null => {
  switch (status) {
    case EmployeeStatus.APPROVED:
      return { text: 'Approved', appearance: Appearance.SUCCESS };
    case EmployeeStatus.INVITED:
      if (actionRequired === EmployeeActionRequiredType.EMPLOYER_APPROVAL) {
        return { text: 'Pending Approval', appearance: Appearance.WARNING };
      }
      if (actionRequired && actionRequired !== EmployeeActionRequiredType.NONE) {
        return { text: startCase(actionRequired), appearance: Appearance.WARNING };
      }
      return { text: 'Invited', appearance: Appearance.WARNING };
    case EmployeeStatus.DISMISSED:
      return { text: 'Dismissed', appearance: Appearance.DANGER };
    default:
      return null;
  }
};

/**
 * Groups the decorated onboarding employees returned by the process endpoint into the summary
 * buckets the onboarding modal renders. Grouping is by the candidate's `onboard.status` so the
 * summary mirrors the categories the user reviewed; any status outside the explicit set
 * (`exist`, `constraint_violation`, `conflict`, etc.) falls through to the `ineligible` bucket.
 *
 * @param {IProcessedOnboardingEmployee[]} employees - Decorated employees from the process response.
 * @returns {IProcessOnboardingEmployeesResponse} - Employees grouped into summary buckets.
 */
export const buildOnboardingSummary = (employees: IProcessedOnboardingEmployee[]): IProcessOnboardingEmployeesResponse => {
  const summary: IProcessOnboardingEmployeesResponse = {
    toCreate: [],
    toLink: [],
    toReactivate: [],
    toReactivateSite: [],
    toSkip: [],
    addSiteRole: [],
    ineligible: [],
  };

  employees.forEach((employee) => {
    switch (employee.onboard.status) {
      case OnboardingEmployeeStatus.NEW:
        summary.toCreate.push(employee);
        break;
      case OnboardingEmployeeStatus.LINK:
        summary.toLink.push(employee);
        break;
      case OnboardingEmployeeStatus.REACTIVATE:
        summary.toReactivate.push(employee);
        break;
      case OnboardingEmployeeStatus.REACTIVATE_SITE:
        summary.toReactivateSite.push(employee);
        break;
      case OnboardingEmployeeStatus.SKIP:
        summary.toSkip.push(employee);
        break;
      case OnboardingEmployeeStatus.ADD_SITE_ROLE:
        summary.addSiteRole.push(employee);
        break;
      default:
        summary.ineligible.push(employee);
    }
  });

  return summary;
};

// Users Tab Config
export const ALL_STATUSES = ['approved', 'invited', 'pending', 'expired', 'dismissed'];
export const ACTION_REQUIRED_TYPES = [EmployeeActionRequiredType.EMPLOYER_APPROVAL, EmployeeActionRequiredType.INVITE_EXPIRED, EmployeeActionRequiredType.INVITE_REQUIRED, EmployeeActionRequiredType.INVITE_SEND_FAILURE];
export const DEFAULT_STATUS_FILTERS = ['approved', 'invited', 'pending', 'expired'];

export const PENDING_STATUS_FILTERS = [
  { key: 'status', operator: ApiQueryOperator.in, value: [EmployeeStatus.INVITED, EmployeeStatus.ACTION_REQUIRED] },
  { key: 'action_required', operator: ApiQueryOperator.in, value: EmployeeActionRequiredType.EMPLOYER_APPROVAL },
];

export const EXPIRED_STATUS_FILTERS = [
  { key: 'status', operator: ApiQueryOperator.in, value: [EmployeeStatus.INVITED, EmployeeStatus.ACTION_REQUIRED] },
  { key: 'action_required', operator: ApiQueryOperator.in, value: [EmployeeActionRequiredType.INVITE_EXPIRED, EmployeeActionRequiredType.INVITE_REQUIRED] },
];

export const INVITE_FAILURE_STATUS_FILTERS = [
  { key: 'status', operator: ApiQueryOperator.in, value: [EmployeeStatus.INVITED, EmployeeStatus.ACTION_REQUIRED] },
  { key: 'action_required', operator: ApiQueryOperator.in, value: EmployeeActionRequiredType.INVITE_SEND_FAILURE },
];

export const ACTION_REQUIRED_FILTERS = [
  { key: 'status', operator: ApiQueryOperator.in, value: EmployeeStatus.ACTION_REQUIRED },
  { key: 'action_required', operator: ApiQueryOperator.in, value: ACTION_REQUIRED_TYPES },
];

/**
 * Maps UI status checkbox values to API query params (status + action_required).
 */
const UI_STATUS_TO_API: Record<string, { statuses: EmployeeStatus[]; actionRequired: EmployeeActionRequiredType[] }> = {
  approved: { statuses: [EmployeeStatus.APPROVED], actionRequired: [EmployeeActionRequiredType.NONE] },
  invited: {
    statuses: [EmployeeStatus.INVITED, EmployeeStatus.ACTION_REQUIRED],
    actionRequired: [EmployeeActionRequiredType.INVITE_SEND_FAILURE, EmployeeActionRequiredType.NONE],
  },
  pending: {
    statuses: [EmployeeStatus.ACTION_REQUIRED],
    actionRequired: [EmployeeActionRequiredType.EMPLOYER_APPROVAL],
  },
  expired: {
    statuses: [EmployeeStatus.ACTION_REQUIRED],
    actionRequired: [EmployeeActionRequiredType.INVITE_EXPIRED, EmployeeActionRequiredType.INVITE_REQUIRED],
  },
  dismissed: { statuses: [EmployeeStatus.DISMISSED], actionRequired: [EmployeeActionRequiredType.NONE] },
};

/**
 * Updates the query filters for status and action_required, based on the selected UI status labels.
 *
 * It removes any existing `status` / `action_required` filters from the incoming
 * query list and replaces them with a normalized combination derived from the
 * `newStatus` checkbox values using `UI_STATUS_TO_API`. All non-status filters
 * (name, role, job title) are preserved.
 *
 * When no statuses are selected, it applies a `status` `nin` filter so that the
 * backend returns an empty result set (matching the "no statuses selected" UX).
 *
 * @param {string[]} newStatus - Array of selected status labels from the UI.
 * @param {IApiQueryParam[]} [query] - Existing API query params to update.
 * @returns {IApiQueryParam[]} New query list with updated status filters.
 */
export const updateStatusFilters = (newStatus: string[], query: IApiQueryParam[] = []): IApiQueryParam[] => {
  const otherFilters = (query || []).filter((q) => q.key !== 'status' && q.key !== 'action_required');

  const statusSet = new Set<EmployeeStatus>();
  const actionRequiredSet = new Set<EmployeeActionRequiredType>();
  newStatus.forEach((uiStatus) => {
    const mapping = UI_STATUS_TO_API[uiStatus];
    if (mapping) {
      mapping.statuses.forEach((s) => statusSet.add(s));
      mapping.actionRequired.forEach((a) => actionRequiredSet.add(a));
    }
  });

  const statusFilters = Array.from(statusSet);
  const actionRequired = Array.from(actionRequiredSet);

  const filters: IApiQueryParam[] = [...otherFilters];

  if (statusFilters.length === 0) {
    filters.push({
      key: 'status',
      operator: ApiQueryOperator.nin,
      value: [EmployeeStatus.APPROVED, EmployeeStatus.INVITED, EmployeeStatus.ACTION_REQUIRED, EmployeeStatus.DISMISSED],
    });
  } else {
    filters.push({ key: 'status', operator: ApiQueryOperator.in, value: statusFilters });
  }

  if (actionRequired.length > 0) {
    filters.push({ key: 'action_required', operator: ApiQueryOperator.in, value: actionRequired });
  }

  return filters;
};
